import { Box, Button, Divider, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useSetAtom } from 'jotai';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useAuth from '../hooks/useAuth/useAuth';
import { usePreLoginMutation } from '../mutations/__generated__/preLogin.generated';
import { RoutePaths } from '../routes/AppRoutes';
import { preLoginEmailAtom } from './state';
import GoogleLoginButton from './GoogleLoginButton';
import { useOIDC } from './hooks/useOIDC';

function LoginForm(): React.JSX.Element {
  const { t } = useTranslation(['common', 'login']);
  const { isLoading } = useAuth();
  const { startOIDC } = useOIDC();

  const [email, setEmail] = useState<string>('');
  const setPreLoginEmail = useSetAtom(preLoginEmailAtom);
  const [preLogin, preLoginOutput] = usePreLoginMutation();
  const navigate = useNavigate();
  const [emailErrorMessage, setEmailErrorMessage] = useState('');

  useEffect(() => {
    if (preLoginOutput.data?.preLogin) {
      setPreLoginEmail(email);
      navigate(RoutePaths.PRELOGIN);
      return;
    }
    if (preLoginOutput.error?.message === 'Email not found') {
      setEmailErrorMessage(t('login:error_email_not_found'));
    }
  }, [preLoginOutput]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (import.meta.env.REACT_APP_CARREFOUR_SSO_FEATURE_FLAG) {
      const { matched } = startOIDC(email);

      if (!matched) {
        preLogin({
          variables: {
            email,
          },
        });
      }
    } else {
      preLogin({
        variables: {
          email,
        },
      });
    }
  };

  const handleContactClick = (): void => {
    window.location.href = 'https://www.kheops.io/contactez-nous';
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        width: 340,
      }}
    >
      <Typography variant="titleLarge" sx={{ mb: 2 }}>
        {t('login:log_in_to_your_account')}
      </Typography>
      <TextField
        placeholder={`${t('login:email')}*`}
        variant="filled"
        type="email"
        name="email"
        required
        value={email}
        size="small"
        fullWidth
        onChange={(e) => setEmail(e.target.value)}
        error={Boolean(preLoginOutput.error)}
        helperText={emailErrorMessage}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="end">
                <EmailOutlinedIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          flexDirection: 'column',
          mt: 2,
        }}>
        <Button
          disabled={isLoading}
          type="submit"
          variant="contained"
          fullWidth
        >
          {t('login:log_in')}
        </Button>
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
          }}
        >
          <Typography variant="bodyMedium" align="center">
            {t('login:dont_have_an_account')}
          </Typography>
          <Button variant="text" onClick={handleContactClick}>
            {t('login:contact_us')}
          </Button>
        </Grid>
      </Grid>
      <Grid sx={{ width: '100%', my: 3 }}>
        <Divider>
          <Typography variant="bodyMedium" align="center">
            {t('common:or')}
          </Typography>
        </Divider>
      </Grid>
      <GoogleLoginButton />
    </Box>
  );
}

export default LoginForm;
